import React, { useState, useRef } from "react";
import { supabase } from "../lib/supabase";
import { S } from "../styles/tokens";
import { useEnter } from "../lib/motion";

export default function AuthView(props) {
  var cardRef = useRef(null);
  useEnter(cardRef, { y: 16, stagger: 0.07 });
  var [mode, setMode] = useState("signin"); // "signin" | "signup" | "forgot"
  var [email, setEmail] = useState("");
  var [password, setPassword] = useState("");
  var [name, setName] = useState("");
  var [loading, setLoading] = useState(false);
  var [err, setErr] = useState("");
  var [msg, setMsg] = useState("");

  async function handleSignIn(e) {
    e.preventDefault();
    setErr(""); setMsg("");
    if (!email || !password) { setErr("Please enter your email and password."); return; }
    setLoading(true);
    var { data, error } = await supabase.auth.signInWithPassword({ email: email.trim(), password });
    setLoading(false);
    if (error) { setErr(error.message); return; }
    // Fetch user plan from DB
    var { data: profile } = await supabase.from("users").select("plan").eq("id", data.user.id).single();
    var plan = (profile && profile.plan) ? profile.plan : "free";
    props.onLogin(data.user, plan);
  }

  async function handleSignUp(e) {
    e.preventDefault();
    setErr(""); setMsg("");
    if (!email || !password || !name) { setErr("Please fill in all fields."); return; }
    if (password.length < 6) { setErr("Password must be at least 6 characters."); return; }
    setLoading(true);
    var { data, error } = await supabase.auth.signUp({ email: email.trim(), password, options: { data: { full_name: name } } });
    setLoading(false);
    if (error) { setErr(error.message); return; }
    // Create user row in DB
    if (data.user) {
      await supabase.from("users").upsert({ id: data.user.id, email: email.trim(), name: name, plan: "free", created_at: new Date().toISOString() });
    }
    setMsg("✓ Account created! Check your email to confirm, then sign in.");
    setMode("signin");
  }

  async function handleForgot(e) {
    e.preventDefault();
    setErr(""); setMsg("");
    if (!email) { setErr("Enter your email address."); return; }
    setLoading(true);
    var { error } = await supabase.auth.resetPasswordForEmail(email.trim(), { redirectTo: "https://syntrix.ink/reset" });
    setLoading(false);
    if (error) { setErr(error.message); return; }
    setMsg("✓ Password reset email sent! Check your inbox.");
  }

  return (
    <div style={S.authWrap}>
      <div ref={cardRef} className="auth-card" style={S.authCard}>
        {/* Back button */}
        <div style={{ marginBottom: 20 }}>
          <button onClick={props.onBack} style={{ background: "none", border: "none", color: "#7a6e64", fontFamily: "inherit", fontSize: 12, cursor: "pointer", display: "flex", alignItems: "center", gap: 6, padding: 0 }}>
            ← Back to Home
          </button>
        </div>
        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <div style={{ width: 48, height: 48, background: "#2c2420", borderRadius: 12, display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 14 }}>
            <svg viewBox="0 0 22 22" fill="none" width={26} height={26}>
              <path d="M11 2L19 6.5V15.5L11 20L3 15.5V6.5L11 2Z" stroke="#f7f3ee" strokeWidth="1.5" strokeLinejoin="round" />
              <path d="M11 7V11L14 13" stroke="#c9963a" strokeWidth="1.5" strokeLinecap="round" />
              <circle cx={11} cy={11} r={1.8} fill="#c9963a" />
            </svg>
          </div>
          <div style={{ fontFamily: "Georgia,serif", fontSize: 22, fontWeight: 600, letterSpacing: 3, color: "#2c2420" }}>SYNTRIX</div>
          <div style={{ fontSize: 9, letterSpacing: 3, color: "#7a6e64", marginTop: 2 }}>AI RESUME INTELLIGENCE</div>
        </div>

        {/* Tabs */}
        {mode !== "forgot" && (
          <div style={{ display: "flex", background: "#f7f3ee", borderRadius: 10, padding: 4, marginBottom: 24 }}>
            {["signin", "signup"].map(function(m) {
              return (
                <button key={m} onClick={function() { setErr(""); setMsg(""); setMode(m); }}
                  style={{ flex: 1, padding: "9px 0", borderRadius: 8, border: "none", fontFamily: "inherit", fontSize: 12, fontWeight: 600, letterSpacing: 1, cursor: "pointer", background: mode === m ? "#fefcfa" : "transparent", color: mode === m ? "#2c2420" : "#7a6e64", boxShadow: mode === m ? "0 1px 4px rgba(44,36,32,.08)" : "none", transition: "all .18s" }}>
                  {m === "signin" ? "Sign In" : "Sign Up"}
                </button>
              );
            })}
          </div>
        )}

        {err && <div style={S.errBar}>{err}</div>}
        {msg && <div style={{ background: "rgba(107,143,113,.08)", border: "1px solid rgba(107,143,113,.25)", borderRadius: 10, padding: "11px 14px", fontSize: 12, color: "#6b8f71", marginBottom: 14, textAlign: "center" }}>{msg}</div>}

        {/* Google Button — show on signin and signup */}
        {mode !== "forgot" && (
          <div style={{ marginBottom: 16 }}>
            <button
              onClick={async function() {
                setErr("");
                var { error } = await supabase.auth.signInWithOAuth({
                  provider: "google",
                  options: { redirectTo: "https://syntrix.ink" }
                });
                if (error) setErr(error.message);
              }}
              style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 10, background: "#fff", border: "1.5px solid rgba(180,160,140,0.35)", borderRadius: 100, padding: "12px 0", fontFamily: "inherit", fontSize: 13, fontWeight: 600, color: "#2c2420", cursor: "pointer", marginBottom: 10 }}
            >
              <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
              Continue with Google
            </button>
            <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "14px 0" }}>
              <div style={{ flex: 1, height: 1, background: "rgba(180,160,140,0.25)" }} />
              <span style={{ fontSize: 11, color: "#7a6e64", letterSpacing: 1 }}>OR</span>
              <div style={{ flex: 1, height: 1, background: "rgba(180,160,140,0.25)" }} />
            </div>
          </div>
        )}

        {/* Sign In */}
        {mode === "signin" && (
          <form onSubmit={handleSignIn}>
            <label style={{ fontSize: 10, letterSpacing: 1.5, color: "#7a6e64", display: "block", marginBottom: 6 }}>EMAIL</label>
            <input style={S.authInput} type="email" placeholder="you@example.com" value={email} onChange={function(e) { setEmail(e.target.value); }} autoFocus />
            <label style={{ fontSize: 10, letterSpacing: 1.5, color: "#7a6e64", display: "block", marginBottom: 6 }}>PASSWORD</label>
            <input style={S.authInput} type="password" placeholder="••••••••" value={password} onChange={function(e) { setPassword(e.target.value); }} />
            <button type="submit" style={{ ...S.authBtn, opacity: loading ? 0.6 : 1 }} disabled={loading}>
              {loading ? "Signing in…" : "Sign In →"}
            </button>
            <div style={{ textAlign: "center", marginTop: 16 }}>
              <button type="button" style={S.authLink} onClick={function() { setErr(""); setMsg(""); setMode("forgot"); }}>Forgot password?</button>
            </div>
          </form>
        )}

        {/* Sign Up */}
        {mode === "signup" && (
          <form onSubmit={handleSignUp}>
            <label style={{ fontSize: 10, letterSpacing: 1.5, color: "#7a6e64", display: "block", marginBottom: 6 }}>FULL NAME</label>
            <input style={S.authInput} type="text" placeholder="Your Name" value={name} onChange={function(e) { setName(e.target.value); }} autoFocus />
            <label style={{ fontSize: 10, letterSpacing: 1.5, color: "#7a6e64", display: "block", marginBottom: 6 }}>EMAIL</label>
            <input style={S.authInput} type="email" placeholder="you@example.com" value={email} onChange={function(e) { setEmail(e.target.value); }} />
            <label style={{ fontSize: 10, letterSpacing: 1.5, color: "#7a6e64", display: "block", marginBottom: 6 }}>PASSWORD</label>
            <input style={S.authInput} type="password" placeholder="Min. 6 characters" value={password} onChange={function(e) { setPassword(e.target.value); }} />
            <button type="submit" style={{ ...S.authBtn, opacity: loading ? 0.6 : 1 }} disabled={loading}>
              {loading ? "Creating account…" : "Create Account →"}
            </button>
          </form>
        )}

        {/* Forgot Password */}
        {mode === "forgot" && (
          <form onSubmit={handleForgot}>
            <div style={{ textAlign: "center", marginBottom: 20 }}>
              <div style={{ fontSize: 14, fontFamily: "Georgia,serif", fontWeight: 500, marginBottom: 6 }}>Reset Password</div>
              <div style={{ fontSize: 12, color: "#7a6e64" }}>Enter your email and we'll send a reset link.</div>
            </div>
            <label style={{ fontSize: 10, letterSpacing: 1.5, color: "#7a6e64", display: "block", marginBottom: 6 }}>EMAIL</label>
            <input style={S.authInput} type="email" placeholder="you@example.com" value={email} onChange={function(e) { setEmail(e.target.value); }} autoFocus />
            <button type="submit" style={{ ...S.authBtn, opacity: loading ? 0.6 : 1 }} disabled={loading}>
              {loading ? "Sending…" : "Send Reset Link"}
            </button>
            <div style={{ textAlign: "center", marginTop: 16 }}>
              <button type="button" style={S.authLink} onClick={function() { setErr(""); setMsg(""); setMode("signin"); }}>← Back to Sign In</button>
            </div>
          </form>
        )}

        <div style={{ textAlign: "center", marginTop: 24, fontSize: 10, color: "#7a6e64", letterSpacing: 1 }}>
          🔒 SECURE · SSL ENCRYPTED · DATA NEVER SOLD
        </div>
      </div>
    </div>
  );
}
